<script lang="ts">
import AttachUploader from '@/app/common/components/AttachUploader.vue';
import FlightCard from '@/components/pages/flight-status/flightCard.vue';
import { formatDate } from '@/app/common/dateFormate';
import { STEP_ONE, FORMAT_CHANNEL, LANG_EN, ATTACH_TYPE, FILE_COUNT_LIMIT, REQUEST_TYPE } from '@/app/const';
import { ComplaintBooking, ComplaintInfo } from '@/interface/Complaint.interface';
import { FlightInfo } from '@/interface/FlightInfo.interface';
import { TicketChannel, TicketSelection } from '@/interface/TicketChannel.interface';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { fileService, flightService, ticketChannelService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    components: {
        AttachUploader,
        FlightCard
    },
    setup() {
        const fileSv = fileService;
        const scrollToRef = ref<HTMLElement | null>(null);

        return { fileSv, scrollToRef }
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
        viewMode: {
            type: Boolean,
            default: false
        }, // make field to be readonly
        summaryInfo: {
            type: Boolean,
            default: false
        } // summary the information to be easy to read (ex.hide some section)
    },
    emits: [
        'update:modelValue',
        'displayConfirmDialog',
        'displayAlertDialog',
        'resetValidation',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            currentIndexDeleting: -1 as number,
            departureDatePicker: false,
            stepOne: STEP_ONE,
            channelOptions: [] as TicketChannel[],
            flightResults: [] as FlightInfo[],
            isSearchingFlight: false,
            fileCountLimit: FILE_COUNT_LIMIT,
            confirmFlight: false as boolean,
            en: LANG_EN,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            },
            lang: this.$i18n.locale,
        }
    },
    watch: {
        modelValue(newVal, oldVal) {
            if (newVal) {
                this.model = newVal;
            }
        },
        'model.flight.remarkModel': {
            handler(val: any[]) {
                if (val) {
                    this.channelOptions.forEach((o, index) => {
                        if (o.id === this.model.flight?.channel.mainTicketChannelId) {
                            this.model.flight.channel.mainTicketChannelOptional = o.channelOptional;
                            this.model.flight.channel.mainTicketFormatChannelOptional = o.formatChannelOptional;
                            if (o.formatChannelOptional === FORMAT_CHANNEL.manual)
                                this.model.flight.channel.remark = val[index];
                            else
                                this.model.flight.channel.subTicketChannelId = val[index];
                        }
                    });
                }
            }, deep: true
        },
        async $route(to, from) {
            // await this.getTicketChannels();
        },
        disabled(newVal, oldVal) {
            if (newVal && this.model.flight) {
                this.model.flight.flightInfo = undefined;
                this.flightResults = [];
            }
        },
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        sections(): number[] {
            return this.model.form?.stepOne ?? [];
        },
        headers() {
            return [
                { title: this.$t('t-order'), sortable: false, key: 'index', align: 'center', width: '5%' },
                { title: this.$t('t-file-name'), key: 'fileName' },
                { title: '', key: 'actions', sortable: false, width: '2%' },
            ] as any[];
        },
        maxDepartureDate() {
            const date = new Date();
            date.setFullYear(date.getFullYear() + 1);
            return date;
        },
        disabled() {
            return this.viewMode || (this.model.flight?.flightNo === undefined || this.model.flight?.flightNo === null || this.model.flight.flightNo === '')
                || (this.model.flight?.departureDate === undefined || this.model.flight?.departureDate === null)
        },
    },
    async mounted() {
        if (this.model.flight && !this.isViewDoc()) {
            if (this.model.flight.flightNo && this.model.flight.departureDate && this.model.flight.flightInfo) {
                await this.onSearchFlight(true);
                this.confirmFlight = true;
            }
            else if (this.model.flight.flightNo && this.model.flight.departureDate && !this.model.flight.flightInfo
                && (this.model.flight.bookings || this.model.flight.channel))
                this.confirmFlight = true;
            await this.getTicketChannels();
        } else {
            this.confirmFlight = true;
        }
    },
    methods: {
        async getTicketChannels() {
            try {
                let res = await ticketChannelService.getAllMainTicketChannels();
                if (res && res.status == 200) {
                    this.channelOptions = res.data as TicketChannel[];
                    if (this.model.flight) {
                        this.model.flight.remarkModel = [];
                        for (let i = 0; i < this.channelOptions.length; i++) {
                            if (this.model.flight.channel.mainTicketChannelId === this.channelOptions[i].id) {
                                if (this.model.flight.channel.subTicketChannelId)
                                    this.model.flight.remarkModel.push(this.model.flight.channel.subTicketChannelId);
                                else
                                    this.model.flight.remarkModel.push(this.model.flight.channel.remark);
                            } else {
                                this.model.flight.remarkModel.push(undefined);
                            }
                        }
                    }
                }
            } catch (error: any) {
            }
        },
        async onSearchFlight(isAuto: boolean = false) {
            if (this.isSearchingFlight || !this.model.flight) return;

            this.isSearchingFlight = true;
            try {
                try {
                    if (!isAuto)
                        this.model.flight.flightInfo = undefined;
                    let res = await flightService.getFlightsWithConditions({
                        flightNo: this.model.flight.flightNo,
                        scheduledDate: this.model.flight.departureDate
                    });
                    if (res && res.status == 200) {
                        let data = res.data as FlightInfo[];
                        if (data && data.length > 0) {
                            this.flightResults = data;
                            this.confirmFlight = false;
                            if (data && data.length === 1) {
                                this.model.flight.flightInfo = this.flightResults[0];
                                this.confirmFlight = true;
                            }
                        }
                        else {
                            this.flightResults = [];
                            this.model.flight.flightInfo = undefined;
                            this.$emit('displayConfirmDialog', {
                                header: `${this.$t('t-flight-searching-not-found')}`,
                                messageTitle: `${this.$t('t-flight-not-fouund-flight-no')} ${this.model.flight.flightNo} ${this.$t('t-date')} ${this.model.flight.departureDateString}`,
                                messageText: undefined,
                                confirmBtnText: this.$t('t-flight-continue'),
                                cancelBtnText: this.$t('t-flight-re-search'),
                                callBack: this.confirmContinueIfNoFlight,
                            } as BaseConfirm);
                        }
                    }
                } catch (error: any) {
                    this.flightResults = [];
                    this.model.flight.flightInfo = undefined;
                    this.$emit('displayConfirmDialog', {
                        header: `${this.$t('t-flight-searching-not-found')}`,
                        messageTitle: `${this.$t('t-flight-not-fouund-flight-no')} ${this.model.flight.flightNo} ${this.$t('t-date')} ${this.model.flight.departureDateString}`,
                        messageText: undefined,
                        confirmBtnText: this.$t('t-flight-continue'),
                        cancelBtnText: this.$t('t-flight-re-search'),
                        callBack: this.confirmContinueIfNoFlight,
                    } as BaseConfirm);
                }
            } finally {
                this.isSearchingFlight = false;
            }
        },
        confirmContinueIfNoFlight() {
            // if (this.validFlightNoFormat(this.model.flight?.flightNo)) {
            this.confirmFlight = true;
            setTimeout(() => {
                this.scrollToBottom();
            });
            // } else {
            //     this.confirmFlight = false;
            //     this.$emit('displayAlertDialog', {
            //         messageTitle: 'Must start with 2 uppercase letters followed by at least 3 numbers.',
            //     } as BaseAlert);
            // }
        },
        addBookingNo() {
            if (this.model.flight) {
                if (this.model.flight.bookings.length < 5) {
                    this.model.flight.bookings.push({
                        bookingNo: undefined,
                        attachments: []
                    })
                } else {
                    this.$emit('displayAlertDialog', {
                        messageTitle: this.$t('t-add-booking-limit'),
                    } as BaseAlert);
                }
            }
        },
        deleteBookingNo() {
            if (this.model.flight) {
                this.model.flight.bookings.splice(this.currentIndexDeleting, 1);
            }
        },
        classBooking(index: number) {
            if (this.model.flight) {
                let c = '';
                c += index != this.model.flight.bookings.length - 1 ? 'mb-3 ' : '';
                c += this.isViewDoc() ? 'request mx-6 mb-4 ' : '';
                return c;
            }
            return '';
        },
        onChannelChanged() {
            if (!this.viewMode && this.model.flight) {
                this.model.flight.remarkModel = this.channelOptions.map(o => undefined);
                this.model.flight.channel.remark = undefined;
                this.model.flight.channel.subTicketChannelId = undefined;
                this.$emit('resetValidation');
            }
        },
        departureDateChange() {
            if (this.model.flight) {
                this.model.flight.departureDateString = formatDate(this.model.flight.departureDate);
            }
        },
        enableSection(code: number) {
            return this.sections.includes(code);
        },
        channelLabel(index: number) {
            return this.lang === LANG_EN ? this.channelOptions[index].channelNameEn : this.channelOptions[index].channelNameTh;
        },
        allBookingAttachments(booking: ComplaintBooking): any[] {
            let newUploads = booking.attachUploads as any[] ?? [];
            newUploads.forEach(up => { up.type = ATTACH_TYPE.NEW });
            let alreadySaved = booking.attachments as any[] ?? [];
            alreadySaved.forEach(saved => { saved.type = ATTACH_TYPE.SAVED });
            return newUploads.concat(alreadySaved);
        },
        deleteAttachment(bookingIndex: number, attach: any) {
            if (this.model.flight) {
                let allAttachs = this.allBookingAttachments(this.model.flight.bookings[bookingIndex]);
                if (allAttachs && allAttachs.length > 0) {
                    if (attach.type === ATTACH_TYPE.NEW) {
                        let newUploads = this.model.flight.bookings[bookingIndex].attachUploads;
                        this.model.flight.bookings[bookingIndex].attachUploads = newUploads?.filter(o => o.name !== attach.name);
                    } else if (attach.type === ATTACH_TYPE.SAVED) {
                        let saved = this.model.flight.bookings[bookingIndex].attachments;
                        this.model.flight.bookings[bookingIndex].attachments = saved?.filter(o => o.fileName !== attach.fileName);
                    }
                }
            }
        },
        isOptionSelection(code: string) {
            return code === FORMAT_CHANNEL.option;
        },
        isManualInput(code: string) {
            return code === FORMAT_CHANNEL.manual;
        },
        subChannelName(item: TicketSelection) {
            return this.lang === LANG_EN ? item.channelNameEn : item.channelNameTh;
        },
        confirmDeleteBookingNo() {
            this.$emit('displayConfirmDialog', {
                messageTitle: `${this.$t('t-you-need')}${this.$t('t-delete')}${this.$t('t-flight-booking-no')}${this.$t('t-or-not')}`,
                messageText: undefined,
                callBack: this.deleteBookingNo
            } as BaseConfirm)
        },
        isViewDoc() {
            return this.viewMode && this.summaryInfo;
        },
        isManage() {
            return this.viewMode && !this.summaryInfo;
        },
        isMigration() {
            return this.model.requestType === REQUEST_TYPE.MIGRATION;
        },
        onFlightSelected(flight: FlightInfo) {
            if (this.model.flight) {
                this.model.flight.flightInfo = flight;
                this.confirmFlight = true;
            }
        },
        isFlightSelected(flight: FlightInfo) {
            let result = this.model.flight?.flightInfo?.flightNo === flight.flightNo
                && this.model.flight?.flightInfo?.flightType === flight.flightType
                && this.model.flight?.flightInfo?.operateDate === flight.operateDate
                && this.model.flight?.flightInfo?.depAirPortCodeIcao === flight.depAirPortCodeIcao
                && this.model.flight?.flightInfo?.arrAirPortCodeIcao === flight.arrAirPortCodeIcao
            return result;
        },
        checkDuplicateFileName() {
            let fileNameList = [] as string[];
            this.model.flight?.bookings.forEach(booking => {
                let allAttach = this.allBookingAttachments(booking);
                let allFilesOfBooking = allAttach.map(o => {
                    if (o.type === ATTACH_TYPE.NEW) {
                        return o.name;
                    } else if (o.type === ATTACH_TYPE.SAVED) {
                        return o.fileName;
                    }
                })
                allFilesOfBooking.forEach(fileName => {
                    if (fileNameList.includes(fileName)) {
                        this.$emit('displayAlertDialog', {
                            messageTitle: this.$t('t-cannot-duplicate-file-name'),
                            messageText: this.$t('t-please-change-file-name'),
                        } as BaseAlert);
                        let index = booking.attachUploads?.findIndex(o => o.name === fileName) as number;
                        booking.attachUploads?.splice(index, 1);
                        return;
                    } else {
                        fileNameList.push(fileName);
                    }
                })
            })
        },
        overSizeLimit() {
            this.$emit('displayAlertDialog', {
                messageTitle: this.$t('t-cannot-upload-over-size-limit'),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert);
        },
        errorEmptyFile() {
            this.$emit('displayAlertDialog', {
                display: true,
                messageTitle: this.$t("t-attachment-error-0byte"),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert);
        },
        scrollToBottom(): void {
            // window.scrollTo({
            //     top: document.body.scrollHeight,
            //     behavior: "smooth", // Makes the scroll smooth
            // });
            const targetSection = this.scrollToRef as HTMLElement | null;
            if (targetSection) {
                targetSection.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            } else {
                console.error("Target section not found");
            }
        },
        // Regular expression: 2 uppercase letters followed by any number of digits
        // validFlightNoFormat(v: any) {
        //     return /^[A-Z]{2}\d{3,}$/.test(v);
        // },
        // Ensure real-time validation and restrict invalid input
        validateInput() {
            if (this.model.flight) {
                let value = this.model.flight.flightNo ?? '';
                let newValue = value.toUpperCase(); // Convert to uppercase

                // if (newValue.length <= 2) {
                //     // Only allow uppercase letters for the first two characters
                //     newValue = newValue.replace(/[^A-Z]/g, '');
                // } else {
                //     // After the first two characters, allow only numbers
                //     const prefix = newValue.slice(0, 2).replace(/[^A-Z]/g, '');
                //     const numbers = newValue.slice(2).replace(/\D/g, ''); // Remove non-digit characters
                //     newValue = prefix + numbers;
                // }

                newValue = newValue.replace(/[^a-zA-Z0-9]/g, '');

                this.model.flight.flightNo = newValue; // Update the model
            }
        }
    },
};
</script>
<template>
    <!-- Flight Detail -->
    <v-card v-if="model && model.flight && enableSection(stepOne.Detail) && (!summaryInfo || confirmFlight)"
        class="app-card" :class="isViewDoc() ? 'hide-card' : isManage() ? 'inner-card mx-6' : ''">
        <v-toolbar v-if="!summaryInfo" density="compact">
            <v-toolbar-title class="ml-0">{{ $t('t-complaint-flight-information') }}</v-toolbar-title>
        </v-toolbar>
        <v-divider v-if="!summaryInfo"></v-divider>
        <v-card-text :class="summaryInfo && !model.flight.flightInfo ? 'pa-0' : ''">
            <v-row>
                <v-col v-if="!summaryInfo" cols="12" class="mx-1 pb-0">
                    <v-row class="pr-2 align-center mb-2">
                        <v-col :cols="12" :lg="summaryInfo ? 12 : 11">
                            <v-row>
                                <v-col cols="12" lg="6">
                                    <div class="text-label">{{ $t('t-flight-date-fly') }} *</div>
                                    <v-menu v-model="departureDatePicker" :close-on-content-click="false"
                                        transition="scale-transition" class="list-modal" :readonly="viewMode">
                                        <template #activator="{ props }">
                                            <v-text-field v-model="model.flight.departureDateString"
                                                append-inner-icon="mdi-calendar-blank-outline" class="hide-float-label"
                                                v-bind:placeholder="$t('t-please-select')" v-bind="props"
                                                variant="outlined" density="comfortable" :clearable="!viewMode"
                                                @click:clear="model.flight.departureDate = undefined" readonly
                                                hide-details="auto">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker v-if="!viewMode" v-model="model.flight.departureDate"
                                            elevation="24" :hide-header="true" :max="maxDepartureDate"
                                            @update:model-value="departureDatePicker = false; departureDateChange();"></v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col cols="12" lg="6">
                                    <div class="text-label">{{ $t('t-flight-number') }} *
                                        <span class="text-primary" style="font-size: small;">
                                            <v-icon icon="" size="x-small" class="ml-1">mdi-information-outline</v-icon>
                                            หลังจากกรอกเลขที่เที่ยวบิน กดค้นหาเพื่อแสดงรายละเอียดเที่ยวบิน
                                        </span>
                                    </div>
                                    <v-text-field v-model.trim="model.flight.flightNo" variant="outlined"
                                        class="hide-float-label" density="comfortable" :readonly="viewMode"
                                        maxlength="9" v-bind:placeholder="$t('t-please-input')" hide-details="auto"
                                        :clearable="!viewMode"
                                        @input="validateInput(); flightResults = []; model.flight.flightInfo = undefined">
                                    </v-text-field>
                                    <div class="flight-number-hint">
                                        ({{ $t('t-form-example') }} AA123)
                                    </div>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col v-if="!summaryInfo" cols="12" lg="1" class="pt-4">
                            <v-btn class="btn-search" variant="flat" color="primary" size="large"
                                append-icon="mdi-magnify" @click="onSearchFlight"
                                :disabled="disabled || isSearchingFlight" :loading="isSearchingFlight" block>
                                <div>{{ $t('t-search') }}</div>
                            </v-btn>
                            <!-- TODO: remove later when have flight data from FPOS -->
                            <!-- <v-btn class="btn-search" variant="flat" color="primary" size="large"
                                @click="confirmContinueIfNoFlight" :disabled="disabled" block>
                                <div>{{ $t('t-confirm') }}</div>
                            </v-btn> -->
                        </v-col>
                    </v-row>
                </v-col>
                <v-col v-if="!isViewDoc() && flightResults && flightResults.length > 0" cols="12">
                    <v-alert v-if="flightResults && flightResults.length > 0 && !model.flight.flightInfo"
                        :title="$t('t-complaint-flight-please-select')" type="info" variant="tonal" class="mb-2">
                    </v-alert>
                    <div v-for="(result, index) in flightResults" @click="onFlightSelected(result)"
                        class="flight-selection"
                        :class="index > 0 ? isFlightSelected(result) ? 'mt-3 flight-selected' : 'mt-3' : isFlightSelected(result) ? 'flight-selected' : ''">
                        <FlightCard v-model="model" :item="result" :show-input-airport="true" :view-mode="false"
                            :selected-card="isFlightSelected(result)" :show-flight-detail="false">
                        </FlightCard>
                    </div>
                </v-col>
                <v-col v-if="isViewDoc() && !isMigration()" cols="12">
                    <FlightCard v-model="model" :item="model.flight.flightInfo" :show-input-airport="true"
                        :view-mode="true" :show-flight-detail="false">
                    </FlightCard>
                </v-col>
                <v-col v-if="model.form && !summaryInfo" cols="12">
                    <v-card class="inner-card advise-card">
                        <v-toolbar density="compact">
                            <v-icon icon="mdi mdi-alert-circle-outline mr-2" size="large" color="orange"></v-icon>
                            <v-toolbar-title>{{ $t('t-complaint-guidelines') }}</v-toolbar-title>
                        </v-toolbar>
                        <v-divider></v-divider>
                        <v-card-text class="formatted">
                            <div v-html="model.form.guidelinesAirlines"></div>
                            <div ref="scrollToRef"></div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-card-text>

    </v-card>
    <!-- Booking -->
    <v-card class="app-card"
        v-if="model && model.flight && enableSection(stepOne.Detail) && (viewMode || confirmFlight)"
        :class="isViewDoc() ? !model.flight.flightInfo ? 'pa-0 ma-0 border-0 radius-0 pt-6' : 'pa-0 ma-0 border-0 radius-0' : isManage() ? 'inner-card mx-6 mb-6' : ''">
        <v-toolbar v-if="!summaryInfo" density="compact">
            <v-toolbar-title class="ml-0">
                {{ $t('t-flight-booking-no') }} {{ $t('t-flight-booking-no-eng') }}
            </v-toolbar-title>
        </v-toolbar>
        <v-divider v-if="!summaryInfo"></v-divider>
        <v-card-text class="pb-0 px-6" :class="!summaryInfo ? 'pt-6' : 'pt-0'">
            <v-alert v-if="!summaryInfo && model.flight.bookings && model.flight.bookings.length > 0" border="start"
                border-color="primary" :title="$t('t-upload-booking-file-limit')" density="compact" variant="tonal"
                class="mb-3" color="primary">
                <template v-slot:text>
                    <div class="d-flex flex-column">
                        <span>{{ $t('t-attachment-message') }}</span>
                        <span v-if="lang === en">{{ $t('t-attachment-message-sub') }}</span>
                    </div>
                </template>
            </v-alert>
            <v-card v-for="(booking, index) in model.flight.bookings" class="booking-card" :class="classBooking(index)">
                <v-card-text>
                    <v-row>
                        <v-col :cols="12" :lg="summaryInfo ? 12 : 7">
                            <div class="text-label py-1">
                                {{ $t('t-flight-booking-no') }}
                                {{ $t('t-flight-booking-no-eng') }}
                                {{ $t('t-at') }} {{ index + 1 }} *</div>
                            <v-text-field v-model.trim="booking.bookingNo" density="comfortable" variant="outlined"
                                hide-details="auto" class="hide-float-label" :label="$t('t-please-input')"
                                :readonly="viewMode" @input="booking.bookingNo = booking.bookingNo?.slice(0, 100)"
                                :rules="[rules.required(`${$t('t-please-fill')}${$t('t-flight-booking-no')}`)]"></v-text-field>
                        </v-col>
                        <v-col v-if="!summaryInfo" cols="12" lg="5" class="w-100 booking-upload-column">
                            <div class="text-label py-1 booking-upload-label" aria-hidden="true">&nbsp;</div>
                            <div class="booking-upload-row d-flex align-center">
                                <AttachUploader v-model="booking.attachUploads" fileType="attach"
                                    class="booking-attach-uploader" :full-height="false" density="comfortable"
                                    :disabled="viewMode || allBookingAttachments(booking).length >= fileCountLimit"
                                    @on-upload-success="checkDuplicateFileName" @on-over-size-limit="overSizeLimit"
                                    @on-error-empty-file="errorEmptyFile">
                                </AttachUploader>
                                <div v-if="!viewMode"
                                    class="booking-upload-delete d-flex align-center justify-center pr-4">
                                    <v-btn icon="mdi mdi-trash-can-outline" color="primary" variant="text"
                                        @click="currentIndexDeleting = index; confirmDeleteBookingNo();"></v-btn>
                                </div>
                            </div>
                        </v-col>
                        <v-col v-if="!summaryInfo" v-for="(attach, attachIndex) in allBookingAttachments(booking)"
                            cols="12" sm="4" lg="4">
                            <div class="attach-items-display mb-2 py-3 px-3">
                                <v-row>
                                    <v-col cols="2" class="file-icon d-flex align-center justify-center"
                                        @click="fileSv.viewFileInEditMode(attach)" style="cursor: pointer">
                                        <v-icon icon="mdi mdi-file-outline" size="xx-large"></v-icon>
                                    </v-col>
                                    <v-col cols="8">
                                        <div class="d-flex flex-column">
                                            <span v-if="fileSv.isNewAttachment(attach.type)">
                                                {{ attach.name }}
                                            </span>
                                            <span v-if="fileSv.isSavedAttachment(attach.type)">
                                                {{ attach.fileName }}
                                            </span>
                                            <span style="color: lightgray">
                                                {{ fileSv.formatFileSize(Number(attach.size)) }}
                                            </span>
                                        </div>
                                    </v-col>
                                    <v-col v-if="!viewMode" cols="2" class="d-flex align-start justify-center">
                                        <v-btn icon="mdi mdi-close" style="color: red"
                                            @click="deleteAttachment(index, attach)"></v-btn>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-col>
                        <v-col v-if="summaryInfo" cols="12">
                            <v-data-table :headers="headers" :items="booking.attachments" hide-default-footer
                                :no-data-text="$t('t-no-data-available')" :items-per-page="-1">
                                <template #[`item.index`]="{ index }">
                                    <div class="w-100 text-center">{{ index + 1 }}</div>
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <div class="actions d-flex align-center">
                                        <v-btn variant="text" color="primary"
                                            @click="fileSv.viewFileAlreadySaved(item.url, item.fileName)">
                                            <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                            <span>{{ $t('t-view-doc') }}</span>
                                        </v-btn>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <div v-if="!viewMode && model.flight.bookings.length < 5"
                class="w-100 border border-dashed d-flex align-center justify-center py-3 add-sub"
                :class="model.flight.bookings && model.flight.bookings.length > 0 ? 'mt-6' : ''" @click="addBookingNo">
                <v-icon icon="mdi mdi-plus mr-1"></v-icon>{{ $t('t-add-booking-no') }}
            </div>
        </v-card-text>
    </v-card>
    <!-- Ticket Channels -->
    <v-card v-if="model && model.flight && enableSection(stepOne.Channel)
        && ((!viewMode && confirmFlight) || (isManage()))" class="app-card" :class="isManage() ? 'mb-0 py-0' : ''">
        <v-toolbar class="px-0" density="compact">
            <v-toolbar-title>{{ $t('t-complaint-ticket-channel') }} *</v-toolbar-title>
        </v-toolbar>
        <v-divider></v-divider>
        <v-card-text class="ticket-channel-selection">
            <!-- <v-container fluid class="pb-0"> -->
            <v-radio-group v-if="channelOptions && channelOptions.length > 0"
                v-model="model.flight.channel.mainTicketChannelId" @update:model-value="onChannelChanged" hide-details
                :readonly="viewMode">
                <v-radio v-for="(channel, index) in channelOptions" :value="channel.id"
                    class="border py-2 px-2 mb-2 channel-box"
                    :class="model.flight.channel.mainTicketChannelId === channel.id ? 'active' : ''">
                    <template v-slot:label>
                        <v-row class="d-flex align-center border channel-label"
                            :class="channel.channelOptional ? 'optional' : ''">
                            <v-col cols="12" :lg="channel.channelOptional ? 7 : 12">
                                <div class="pl-2">{{ channelLabel(index) }}</div>
                            </v-col>
                            <v-col cols="12" lg="5" class="px-3 py-3 app-form-input d-flex align-center"
                                v-if="channel.channelOptional && isOptionSelection(channel.formatChannelOptional) && model.flight.remarkModel">
                                <div class="text-label mr-2">{{ $t('t-please-fill') }}</div>
                                <v-select v-model="model.flight.remarkModel[index]"
                                    class="channel-selection hide-float-label py-3" variant="outlined"
                                    :label="$t('t-please-select')" hide-details="auto"
                                    :items="channel.ticketChannelSubs" item-value="id" density="comfortable"
                                    :readonly="viewMode"
                                    :disabled="model.flight.channel.mainTicketChannelId !== channel.id"
                                    :rules="model.flight.channel.mainTicketChannelId === channel.id ? [rules.required(`${$t('t-please-select')}${$t('t-complaint-ticket-channel')}`)] : []">
                                    <template v-slot:item="{ props, item }">
                                        <v-list-item v-bind="props" height="2000" :title="subChannelName(item.raw)">
                                        </v-list-item>
                                    </template>
                                    <template v-slot:selection="{ item }">
                                        <div v-if="item.raw.id">{{ subChannelName(item.raw) }}</div>
                                    </template>
                                </v-select>
                            </v-col>
                            <v-col cols="12" lg="5" class="px-3 py-3 app-form-input d-flex align-center"
                                v-if="channel.channelOptional && isManualInput(channel.formatChannelOptional) && model.flight.remarkModel">
                                <div class="text-label mr-2">{{ $t('t-please-fill') }}</div>
                                <v-text-field v-model="model.flight.remarkModel[index]" variant="outlined"
                                    class="channel-selection hide-float-label py-3" density="comfortable"
                                    :readonly="viewMode" v-bind:placeholder="$t('t-please-input')" hide-details="auto"
                                    :disabled="model.flight.channel.mainTicketChannelId !== channel.id"
                                    :rules="model.flight.channel.mainTicketChannelId === channel.id ? [rules.required(`${$t('t-please-fill')}${$t('t-complaint-ticket-channel')}`)] : []">
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </template>
                </v-radio>
            </v-radio-group>
            <!-- </v-container> -->
        </v-card-text>
    </v-card>
</template>

<style scoped>
.flight-number-hint {
    margin-top: 4px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 12px;
    line-height: 1.2;
}

.booking-upload-label {
    visibility: hidden;
}

.booking-upload-row {
    gap: 0;
}

.booking-upload-delete {
    flex: 0 0 16.6667%;
}

.booking-attach-uploader {
    flex: 1 1 auto;
    height: 48px !important;
}

.booking-attach-uploader :deep(.v-input__control),
.booking-attach-uploader :deep(.v-field) {
    height: 100%;
    min-height: 48px;
}

.booking-attach-uploader :deep(.attach-text > div:last-child span) {
    font-size: 10px;
}

.booking-attach-uploader :deep(.v-field--variant-outlined .v-field__outline__start),
.booking-attach-uploader :deep(.v-field--variant-outlined .v-field__outline__notch::before),
.booking-attach-uploader :deep(.v-field--variant-outlined .v-field__outline__notch::after),
.booking-attach-uploader :deep(.v-field--variant-outlined .v-field__outline__end) {
    border-color: #e2e6eb;
    opacity: 1;
}

@media (max-width: 992px) {
    .booking-upload-label {
        display: none;
    }
}
</style>
